import React, { useState, useEffect, useRef } from "react";

/**
 * Editable text wrapper. When editor mode is active and onChange is provided,
 * clicking enables contentEditable and saves on blur.
 *
 * When the value is empty AND editable is on, a placeholder is rendered so
 * admins can clearly see and click the empty field.
 */
export const Editable = ({
  as: Tag = "span",
  value,
  onChange,
  editable = false,
  multiline = false,
  className = "",
  testId,
  placeholder = "Click to edit…",
}) => {
  const ref = useRef(null);
  const [editing, setEditing] = useState(false);

  const isEmpty = !value || (typeof value === "string" && value.trim() === "");

  useEffect(() => {
    if (ref.current && !editing) {
      // While not editing: show value, or the placeholder text if empty
      ref.current.innerText = isEmpty ? placeholder : value;
    }
  }, [value, editing, placeholder, isEmpty]);

  if (!editable) {
    // Public site: never show placeholder, only the actual value
    return (
      <Tag className={className} data-testid={testId}>
        {value || ""}
      </Tag>
    );
  }

  // Visual hint when empty so admin can clearly see the click target
  const placeholderStyle = isEmpty && !editing
    ? "italic text-ivory/35 border-b border-dashed border-gold/40"
    : "";

  return (
    <Tag
      ref={ref}
      data-testid={testId}
      data-editable="true"
      className={`${className} ${placeholderStyle} focus:outline focus:outline-2 focus:outline-gold rounded-sm cursor-text`}
      contentEditable={editing}
      suppressContentEditableWarning
      onClick={() => {
        if (!editing) {
          setEditing(true);
          // Clear the placeholder text on first click so user starts with empty input
          if (isEmpty && ref.current) {
            ref.current.innerText = "";
          }
        }
      }}
      onFocus={() => {
        setEditing(true);
        if (isEmpty && ref.current && ref.current.innerText === placeholder) {
          ref.current.innerText = "";
        }
      }}
      onBlur={(e) => {
        setEditing(false);
        const txt = e.currentTarget.innerText.trim();
        if (txt !== (value || "")) onChange && onChange(txt);
      }}
      onKeyDown={(e) => {
        if (!multiline && e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
    />
  );
};

export default Editable;
